import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LuHouse,
  LuBanknote,
  LuGraduationCap,
  LuPlane,
  LuCoins,
  LuPhone,
  LuMenu,
  LuX,
  LuUser
} from "react-icons/lu";
import logo from "../assets/logowithoutmoto.jpg";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Handle Scroll to toggle Navbar vs Burger
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const goSection = (id) => {
    if (location.pathname === "/") {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
    setMobileOpen(false);
  };

  const navItems = [
    { icon: <LuHouse />, label: "Home", action: () => goSection("home"), path: "/" },
    { icon: <LuBanknote />, label: "Loans", action: () => navigate("/loans"), path: "/loans" },
    { icon: <LuGraduationCap />, label: "Education", action: () => navigate("/education-support"), path: "/education-support" },
    { icon: <LuPlane />, label: "Travel", action: () => navigate("/travel-transport"), path: "/travel-transport" },
    { icon: <LuCoins />, label: "Gold Plus", action: () => navigate("/gold-plus"), path: "/gold-plus" },
    { icon: <LuUser />, label: "Our Team", action: () => navigate("/our-team"), path: "/our-team" },
  ];

  return (
    <>
      {/* FLOATING PILL NAVBAR (Visible when NOT scrolled) */}
      <AnimatePresence>
        {!scrolled && (
          <motion.nav
            initial={{ y: -100, opacity: 0, x: "-50%" }}
            animate={{ y: 0, opacity: 1, x: "-50%" }}
            exit={{ y: -100, opacity: 0, x: "-50%" }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="desktop-nav-pill floating-nav-container"
          >
            {/* Logo Area */}
            <div
              className="nav-logo"
              onClick={() => goSection("home")}
              style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', paddingRight: '20px', borderRight: '1px solid rgba(0,0,0,0.06)' }}
            >
              <img src={logo} alt="RAMB" style={{ height: '42px', objectFit: 'contain' }} />
              <span style={{ fontFamily: "'Great Vibes', cursive", fontSize: '22px', color: 'rgba(69, 195, 235, 1)', whiteSpace: 'nowrap' }}>
                Your success is our interest
              </span>
            </div>

            {/* Desktop Icons */}
            <div className="desktop-menu" style={{ display: 'flex', gap: '12px' }}>
              {navItems.map((item, index) => (
                <NavIconButton
                  key={index}
                  icon={item.icon}
                  label={item.label}
                  onClick={item.action}
                  isActive={location.pathname === item.path}
                />
              ))}
            </div>

            {/* Contact Button */}
            <button
              onClick={() => goSection("contact")}
              style={{
                background: 'var(--blue2)',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                padding: '12px 24px',
                fontWeight: '700',
                fontSize: '14px',
                cursor: 'pointer',
                boxShadow: '0 6px 16px rgba(37, 52, 56, 1)', // Darker shadow as per user
                marginLeft: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <LuPhone size={14} /> Contact
            </button>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* PERSISTENT BURGER BUTTON */}
      <button
        className={`burger-trigger-btn ${scrolled || mobileOpen ? "active" : ""}`}
        onClick={() => setMobileOpen(!mobileOpen)}
      >
        {mobileOpen ? <LuX /> : <LuMenu />}
      </button>

      {/* MOBILE DRAWER / FULL MENU OVERLAY */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "300px",
              height: "100%",
              background: "rgba(253, 254, 254, 0.83)",
              backdropFilter: "blur(10px)",
              zIndex: 1000,
              boxShadow: "-10px 0 40px rgba(0,0,0,0.1)",
              padding: "90px 30px",
              display: "flex",
              flexDirection: "column",
              gap: "20px"
            }}
          >
            {navItems.map((item, index) => (
              <div
                key={index}
                onClick={() => { item.action(); setMobileOpen(false); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '18px',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#45c3eb', // Always motto color as requested
                  cursor: 'pointer',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  transition: 'all 0.2s',
                  border: '1px solid transparent',
                  background: location.pathname === item.path ? 'rgba(69, 195, 235, 0.08)' : 'transparent',
                  borderLeft: location.pathname === item.path ? '4px solid #45c3eb' : '4px solid transparent' // Mobile active left bar
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.background = "#f3f4f6";
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.background = "transparent";
                  }
                }}
              >
                <div style={{
                  color: location.pathname === item.path ? "#ffffff" : "#45c3eb",
                  background: location.pathname === item.path ? "#45c3eb" : "rgba(69, 195, 235, 0.1)",
                  padding: "8px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  {item.icon}
                </div>
                {item.label}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function NavIconButton({ icon, label, onClick, isActive }) {
  return (
    <button
      className={`nav-icon-btn ${isActive ? 'active' : ''}`}
      onClick={onClick}
    >
      <span style={{ fontSize: '22px', marginBottom: '6px' }}>{icon}</span>
      <span style={{ fontSize: '12px', fontWeight: '600' }}>{label}</span>
      {isActive && (
        <motion.div
          layoutId="nav-underline"
          style={{
            position: 'absolute',
            bottom: '4px',
            left: '25%',
            right: '25%',
            height: '3px',
            background: '#45c3eb',
            borderRadius: '4px',
          }}
          initial={false}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        />
      )}
    </button>
  )
}
